/**
 * Barber Assignment Service - Calculate which barber will be auto-assigned
 */

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Barber, BarberSchedule, BarberStatus } from '@/types';
import { isBreakActive } from '@/lib/breaks';

type DayOfWeek = keyof BarberSchedule;

/**
 * Get current day of week as lowercase string
 */
function getTodayDayName(): DayOfWeek {
  const daysOfWeek: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return daysOfWeek[new Date().getDay()];
}

/**
 * Check if a barber is working today based on their schedule
 */
function isBarberWorkingToday(barber: Barber): boolean {
  const today = getTodayDayName();
  const schedule = barber.schedule;

  if (!schedule || !schedule[today]) {
    return false; // If no schedule defined, assume NOT working (matches server logic)
  }

  return schedule[today].isWorking === true;
}

/**
 * Calculate which barber will be auto-assigned for "any" selection
 * This replicates the Cloud Function logic on the client side for preview
 */
export async function calculateAutoAssignedBarber(
  branchId: string
): Promise<Barber | null> {
  try {
    console.log('📊 Calculating auto-assigned barber for branch:', branchId);
    const today = getTodayDayName();

    // Get all active barbers from this branch
    const barbersQuery = query(
      collection(db, 'barbers'),
      where('branchId', '==', branchId),
      where('isActive', '==', true)
    );

    const barbersSnapshot = await getDocs(barbersQuery);

    if (barbersSnapshot.empty) {
      console.log('❌ No active barbers found in branch');
      return null;
    }

    // Get barber status for all barbers to check for day_off
    const barberIds = barbersSnapshot.docs.map(d => d.id);
    const barberStatusPromises = barberIds.map(bid =>
      getDoc(doc(db, 'barberStatus', bid))
    );
    const barberStatusDocs = await Promise.all(barberStatusPromises);

    // Create a set of barbers who are unavailable today
    const barbersUnavailableToday = new Set<string>();

    // Check barber status for day_off, on_break, offline.
    // Una pausa marcada por anticipado no aparta al profesional del reparto:
    // sigue cortando hasta que le llegue la hora (#22, #23).
    barberStatusDocs.forEach((statusDoc, index) => {
      if (statusDoc.exists()) {
        const statusData = statusDoc.data() as BarberStatus;
        if (statusData?.breakReason === 'day_off' ||
            statusData?.status === 'offline' ||
            isBreakActive(statusData)) {
          barbersUnavailableToday.add(barberIds[index]);
          console.log(`  ❌ Barber ${barberIds[index]} excluded (status: ${statusData?.status}, reason: ${statusData?.breakReason})`);
        }
      }
    });

    // Count pending tickets per barber (only for available barbers)
    const barberQueueCounts: { [barberId: string]: number } = {};
    const barbersMap: { [barberId: string]: Barber } = {};

    barbersSnapshot.docs.forEach(docSnap => {
      const barber = docSnap.data() as Barber;
      const barberId = docSnap.id;

      // Check 1: Is the barber marked as unavailable? (isAvailable flag)
      if (barber.isAvailable === false) {
        barbersUnavailableToday.add(barberId);
        console.log(`  ❌ Barber ${barber.displayName} excluded (isAvailable = false)`);
        return;
      }

      // Check 2: Is the barber working today according to their schedule?
      if (!isBarberWorkingToday(barber)) {
        barbersUnavailableToday.add(barberId);
        console.log(`  ❌ Barber ${barber.displayName} excluded (not working on ${today})`);
        return;
      }

      // Check 3: Is the barber already marked as unavailable (status)?
      if (barbersUnavailableToday.has(barberId)) {
        return;
      }

      // Barber is available
      barberQueueCounts[barberId] = 0;
      barbersMap[barberId] = barber;
    });

    // Check if there are any available barbers after filtering
    if (Object.keys(barberQueueCounts).length === 0) {
      console.log('❌ No barbers available today after filtering');
      return null;
    }

    // Count tickets for each barber in this branch
    const allTicketsQuery = query(
      collection(db, 'queues'),
      where('branchId', '==', branchId),
      where('status', 'in', ['waiting', 'notified', 'arrived', 'in_service'])
    );

    const allTicketsSnapshot = await getDocs(allTicketsQuery);

    allTicketsSnapshot.docs.forEach(docSnap => {
      const ticket = docSnap.data();
      if (ticket.barberId && ticket.barberId in barberQueueCounts) {
        barberQueueCounts[ticket.barberId]++;
      }
    });

    // Find barber with least tickets
    let minCount = Infinity;
    let selectedBarberId: string | null = null;

    for (const [bId, count] of Object.entries(barberQueueCounts)) {
      console.log(`  ✅ Barber ${barbersMap[bId].displayName}: ${count} tickets (available)`);
      if (count < minCount) {
        minCount = count;
        selectedBarberId = bId;
      }
    }

    if (selectedBarberId && barbersMap[selectedBarberId]) {
      const selectedBarber = barbersMap[selectedBarberId];
      console.log('✅ Auto-assigned barber:', selectedBarber.displayName, 'with', minCount, 'tickets');
      return selectedBarber;
    }

    // Fallback: return first available barber
    const availableBarberId = Object.keys(barbersMap)[0];
    if (availableBarberId) {
      const fallbackBarber = barbersMap[availableBarberId];
      console.log('⚠️ Using fallback barber:', fallbackBarber.displayName);
      return fallbackBarber;
    }

    console.log('❌ No barbers available for assignment');
    return null;
  } catch (error) {
    console.error('❌ Error calculating auto-assigned barber:', error);
    return null;
  }
}
